import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { DataGridColumn, useSavedSettings } from '../../shared';
import { v5 as uuidv5 } from 'uuid';
import { StoredTableSettings, useStoredSettings } from './hooks/use-stored-settings';
import { enqueueSnackbar } from 'notistack';
import _ from 'lodash';


const MY_NAMESPACE = '1b671a64-40d5-491e-99b0-da01ff1f3341'; // Arbitrary namespace UUID

type Column = DataGridColumn<any, any>;
type ColumnWithId = DataGridColumn<any, any> & {id: string};

interface TableSettingsContextValue<F> {
  tableId: string,
  columns: ColumnWithId[],
  setColumns: (columns: ColumnWithId[]) => void,
  initialColumns: Column[],
  initColumns: (columns: Column[]) => void,
  saved: boolean,
  restoreFields: () => void,
  reset: () => void,
  saveSettings: (name?: string) => void,
  storedSettings: StoredTableSettings<F>[],
  viewName: string,
  setViewName: (name: string) => void,
  deleteView: () => void,
  views: string[],
  filters?: { [key: string]: any },
  setFilters: (filters?: { [key: string]: any }) => void,
}

export const TableSettingsContext = createContext<TableSettingsContextValue<any>>({
  tableId: '',
  initialColumns: [],
  columns: [],
  setColumns: () => {},
  initColumns: () => {},
  restoreFields: () => {},
  reset: () => {},
  viewName: '',
  setViewName: () => {},
  views: [],
  deleteView: () => {},
  saveSettings: () => {},
  storedSettings: [],
  saved: true,
  filters: undefined,
  setFilters: () => {},
});

export function withTableSettings<P>(WrappedComponent: React.ComponentType<P>, tableId: string) {
  return (props: P) => {
    const [savedSettings, setSavedSettings] = useSavedSettings();
    const savedTableSettings = useMemo(() => {
      if (savedSettings.tableSettings && savedSettings.tableSettings[tableId]) {
        return savedSettings.tableSettings[tableId]
      }
      return {
        viewName: '',
        views: [{
          name: '',
          columns: [],
          filters: undefined
        }]
      }
    }, [savedSettings.tableSettings, tableId])

    const [storedSettings, setStoredSetting] = useStoredSettings(tableId, savedTableSettings.views);

    const [initial, setInitial] = useState<ColumnWithId[]>([]);
    const [viewName, setViewName] = useState(savedTableSettings.viewName);

    const [filters, setFilters] = useState<{ [key: string]: any }>();

    const columns = useMemo(() => {
      const stored = storedSettings.find(s => s.name === viewName)?.columns || []
      return stored.length ? stored.map(s => initial.find(c => c.id === s.id)!).filter(Boolean) : initial;
    }, [initial, viewName, storedSettings]);

    // save table settings in session storage
    const storeSettings = (settings: (Partial<StoredTableSettings<any>> & { name: string})[]) => {
      setStoredSetting(prev => {
        const updated = [...prev];

        settings.forEach(item => {
          const index = prev.findIndex(s => s.name === item.name);
          if (index >= 0) {
            updated[index].columns = item.columns === undefined ? updated[index].columns : item.columns;
            updated[index].filters = item.filters === undefined ? updated[index].filters : item.filters;
          } else {
            updated.push({
              ...item,
              columns:  item.columns === undefined ? [] : item.columns,
            })
          }
        })
        return updated;
      })
    }

    const initColumns = (value: Column[]) => {
      setInitial(value.map(f => ({...f, id: uuidv5(f.title, MY_NAMESPACE)})));
    }

    const reset = () => {
      const saved = savedTableSettings.views.find(v => v.name === viewName);
      if (saved) {
        storeSettings([saved]);
      }
    }

    const restoreFields = () => {
      storeSettings([{ columns: initial, name: viewName, filters: undefined }]);
    }

    // save table settings on node server
    const saveSettings = (name?: string) => {
      const toSave = storedSettings.find(s => s.name === viewName);
      const prev = savedTableSettings.views;
      if (toSave) {
        const saveViewName = typeof name === 'string' ? name : viewName;
        const index = prev.findIndex(s => s.name === saveViewName);
        const updated = [...prev];
        if (index >= 0) {
          updated[index] = { ...toSave, filters };
        } else {
          updated.push({ ...toSave, name: saveViewName, filters });

          if (name) {
            // save as clicked - restore previous value to current view and change tab
            const arrToSave: StoredTableSettings<any>[] = [{columns: toSave.columns, name, filters}]

            // do we have saved settings in local storage
            const prevView = prev.find(s => s.name === viewName);
            if (prevView) {
              arrToSave.push({columns: prevView.columns, name: viewName, filters: prevView.filters});
            } else {
              arrToSave.push({columns: [], name: viewName, filters: undefined});
            }

            storeSettings(arrToSave);

            changeViewName(name)
          }
        }
        setSavedSettings({
          tableSettings: {
            [tableId]: {
              viewName: saveViewName,
              views: _.uniqBy(updated, 'name')
            }
          }
        })
      }


      enqueueSnackbar('View saved', {variant: 'success'});
    }

    const saved = useMemo(() => {
      const stored = storedSettings.find(s => s.name === viewName);
      if (!stored || (!stored.columns.length && (!stored.filters || !Object.keys(stored.filters as any).length ))) {
        return true
      }
      return _.isEqual(savedTableSettings.views.find(s => s.name === viewName), storedSettings.find(s => s.name === viewName))
    }, [savedSettings, storedSettings, viewName]);

    const views = useMemo(() => (
      _.uniq([...storedSettings, ...savedTableSettings.views].map(i => i.name).filter(Boolean).sort())
    ), [savedSettings, storedSettings]);

    const deleteView = () => {
      const index = views.indexOf(viewName);
      if (index >= 0) {
        const updated = [...savedTableSettings.views];
        const toDelete = updated.findIndex(s => s.name === viewName);
        if (toDelete >= 0) {
          updated.splice(toDelete, 1);
        }
        const newViewName = index > 0 ? views[index - 1] : ''

        setSavedSettings({
          tableSettings: {
            [tableId]: {
              viewName: newViewName,
              views: _.uniqBy(updated, 'name')
            }
          }
        })

        setStoredSetting(prev => {
          const updated = [...prev];
          const toDelete = updated.findIndex(s => s.name === viewName);
          if (toDelete >= 0) {
            updated.splice(toDelete, 1);
          }
          return updated;
        })
        setViewName(newViewName);
        enqueueSnackbar('Success!', {variant: 'success'});
      }
    }

    const changeViewName = (name: string) => {
      setViewName(name);
      setSavedSettings({
        tableSettings: {
          [tableId]: {
            ...savedTableSettings,
            viewName: name,
          }
        }
      })
    }

    return <TableSettingsContext.Provider value={{
      setFilters: f => {
        setFilters(f)
        storeSettings([{name: viewName, filters: f}])
      },
      restoreFields,
      filters,
      deleteView,
      saveSettings,
      saved,
      views,
      storedSettings,
      viewName,
      setViewName: changeViewName,
      columns,
      setColumns: cols => storeSettings([{name: viewName, columns: cols}]),
      reset,
      initColumns,
      initialColumns: initial,
      tableId
    }}>
      <WrappedComponent {...props} />
    </TableSettingsContext.Provider>
  }
}

export function useTableSettings<F extends { [key: string]: any }>(initialColumns: Column[], filters?: F, onViewChanged?: (settings: StoredTableSettings<F>) => void) {
  const {initColumns, columns, setFilters, viewName, storedSettings} = useContext(TableSettingsContext);

  useEffect(() => {
    initColumns(initialColumns);
  }, [initialColumns]);

  useEffect(() => {
    setFilters(filters);
  }, [filters]);

  useEffect(() => {
    const currentView = storedSettings.find(s => s.name === viewName);
    onViewChanged && currentView && onViewChanged(currentView);
  }, [viewName, storedSettings]);

  return {columns}
}


